import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import './moderation.css';
import {
  actionLabels, checkStaff, decideAppeal, fieldLabels, formatAge, formatDateTime, isOverdue, labelFor, liftSuspension,
  listActions, listAppeals, listReports, listSuspensions, queueSummary, reasonLabels, removeContent, reportStatusLabels,
  restoreContent, setReportStatus, sortQueue, suspendAccount, targetTypeLabels, validateReason,
} from './moderation.js';
import { SignInGate } from '../SignIn.jsx';
import { ADMIN_PATH } from './adminPath.js';

const tabs = [['reports', 'Reports'], ['suspensions', 'Suspensions'], ['appeals', 'Appeals'], ['audit', 'Audit log']];
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Staff-only moderation console. The database refuses every call from non-staff, whatever this page shows. */
export function ModerationConsole() {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let live = true;
    if (!user) { setAccess(null); return undefined; }
    checkStaff(supabase).then((value) => { if (live) setAccess(value); });
    return () => { live = false; };
  }, [user]);

  if (loading) return <div className="content-wrap">Loading account…</div>;
  if (!user) return <SignInGate next={`${ADMIN_PATH}/moderation`}/>;
  if (!access) return <div className="content-wrap">Checking access…</div>;
  if (!access.staff) {
    return (
      <div className="content-wrap account-page">
        <h1>You don't have access</h1>
        <p>This area is for Resident Service 360 moderation staff only.</p>
        <Link to="/account">Back to your account</Link>
      </div>
    );
  }
  return <Console role={access.role} />;
}

export default ModerationConsole;

function Console({ role }) {
  const [tab, setTab] = useState('reports');
  const [summary, setSummary] = useState(null);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((value) => value + 1), []);
  useEffect(() => {
    let live = true;
    queueSummary(supabase).then((value) => { if (live) setSummary(value); }).catch(() => { if (live) setSummary(null); });
    return () => { live = false; };
  }, [version]);

  return (
    <div className="content-wrap account-page">
      <header className="coord-page-heading">
        <div>
          <span className="account-kicker">MODERATION</span>
          <h1>Reports and safety.</h1>
          <p>Signed in as {role === 'admin' ? 'an admin' : 'a moderator'}. Every action is recorded with your name and reason.</p>
        </div>
      </header>
      {summary && (
        <div className="mod-summary" aria-label="Queue summary">
          <div><strong>{summary.open}</strong><span>Open reports</span></div>
          <div><strong>{summary.reviewing}</strong><span>In review</span></div>
          <div className={summary.overdue > 0 ? 'mod-alert' : ''}><strong>{summary.overdue}</strong><span>Waiting over 24 hours</span></div>
          <div><strong>{summary.open_appeals}</strong><span>Open appeals</span></div>
          <div><strong>{summary.active_suspensions}</strong><span>Active suspensions</span></div>
        </div>
      )}
      <div className="segmented mod-tabs" role="tablist" aria-label="Moderation sections">
        {tabs.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === 'reports' && <ReportsTab onChange={refresh} version={version} />}
      {tab === 'suspensions' && <SuspensionsTab onChange={refresh} version={version} />}
      {tab === 'appeals' && <AppealsTab onChange={refresh} version={version} />}
      {tab === 'audit' && <AuditTab version={version} />}
    </div>
  );
}

function useLoader(load, deps) {
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    let live = true;
    load().then((data) => { if (live) setState({ data, error: '' }); }).catch((cause) => { if (live) setState({ data: [], error: cause.message }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

/** A button that opens a small form asking for the required reason before acting. */
function ReasonAction({ label, confirmLabel, hint, onSubmit, withEndDate = false }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    const problem = validateReason(reason);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try {
      await onSubmit(reason.trim(), endsAt ? new Date(endsAt).toISOString() : null);
      setOpen(false); setReason(''); setEndsAt('');
    } catch (cause) { setError(cause.message); } finally { setBusy(false); }
  }
  if (!open) return <button type="button" className="button button-secondary" onClick={() => setOpen(true)}>{label}</button>;
  return (
    <form className="mod-form" onSubmit={submit}>
      <label>Reason (required)<textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy} autoFocus /></label>
      {hint && <span className="mod-note">{hint}</span>}
      {withEndDate && <label>Ends (leave empty for no end date)<input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} disabled={busy} /></label>}
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="mod-action-row">
        <button className="button button-blue" disabled={busy}>{busy ? 'Saving…' : confirmLabel}</button>
        <button type="button" className="button button-secondary" onClick={() => { setOpen(false); setError(''); }} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

function ReportsTab({ onChange, version }) {
  const [status, setStatus] = useState('waiting');
  const [targetType, setTargetType] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const { data, error } = useLoader(
    () => listReports(supabase, { status: status === 'all' ? null : status, targetType: targetType || null, minAgeHours: overdueOnly ? 24 : null, limit: 200 }),
    [status, targetType, overdueOnly, version],
  );
  const reports = sortQueue(data || []);
  const selected = reports.find((row) => row.id === selectedId) || reports[0];

  return (
    <section>
      <div className="mod-filters">
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="waiting">Waiting (open or in review)</option>
          {Object.entries(reportStatusLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          <option value="all">All</option>
        </select></label>
        <label>Content<select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
          <option value="">All content</option>
          {Object.entries(targetTypeLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <label className="mod-check"><input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />Only over 24 hours old</label>
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {data === null ? <p>Loading reports…</p> : reports.length === 0 ? <p className="account-empty">No reports match these filters.</p> : (
        <div className="mod-layout">
          <ul className="mod-queue" aria-label="Reports, oldest first">
            {reports.map((report) => {
              const overdue = isOverdue(report);
              const fields = report.content?.fields || {};
              return (
                <li key={report.id}>
                  <button type="button" className={`${overdue ? 'mod-overdue' : ''} ${selected?.id === report.id ? 'selected' : ''}`} onClick={() => setSelectedId(report.id)}>
                    <strong>{fields.title || fields.name || fields.full_name || fields.caption || labelFor(targetTypeLabels, report.target_type)}</strong>
                    <small>{labelFor(targetTypeLabels, report.target_type)} · {labelFor(reasonLabels, report.reason)} · {labelFor(reportStatusLabels, report.status)}</small>
                    <small>Waiting {formatAge(report.created_at)}{report.reports_on_same_content > 1 ? ` · ${report.reports_on_same_content} reports on this` : ''}</small>
                    {overdue && <span className="mod-flag">Overdue</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {selected && <ReportDetail key={selected.id} report={selected} onChange={onChange} />}
        </div>
      )}
    </section>
  );
}

function FieldRow({ label, value }) {
  return <><dt>{label}</dt><dd>{value}</dd></>;
}

function ContentFields({ content }) {
  if (!content?.available) return <p className="mod-note">{content?.note || 'No content summary is available.'}</p>;
  const entries = Object.entries(content.fields || {}).filter(([, value]) => value !== null && value !== '');
  return (
    <dl className="mod-fields">
      {entries.map(([key, value]) => (
        <FieldRow key={key} label={labelFor(fieldLabels, key)} value={/_at$|deadline/.test(key) ? formatDateTime(value) || String(value) : String(value)} />
      ))}
      {content.author_user_id && <FieldRow label="Posted by" value={`${content.author_name || 'Name not given'}${content.author_suspended ? ' (suspended)' : ''}`} />}
      {content.organization_id && <FieldRow label="Company" value={`${content.organization_name || 'Unnamed company'}${content.organization_suspended ? ' (suspended)' : ''}`} />}
    </dl>
  );
}

function ReportDetail({ report, onChange }) {
  const content = report.content || {};
  const waiting = ['open', 'reviewing'].includes(report.status);
  const [error, setError] = useState('');
  const run = (action) => async (reason, endsAt) => { await action(reason, endsAt); onChange(); };
  function startReview() {
    setError('');
    setReportStatus(supabase, report.id, 'reviewing').then(onChange).catch((cause) => setError(cause.message));
  }
  return (
    <article className="account-content-panel mod-detail" style={{ marginTop: 0 }}>
      <h2>{labelFor(targetTypeLabels, report.target_type)} report</h2>
      <p className="mod-note">
        Reported {formatDateTime(report.created_at)} by {report.reporter_name || 'a signed-in user'} · waiting {formatAge(report.created_at)}
        {isOverdue(report) && <> · <span className="mod-flag">Overdue</span></>}
      </p>
      <dl className="mod-fields">
        <FieldRow label="Reason" value={labelFor(reasonLabels, report.reason)} />
        {report.details && <FieldRow label="Reporter said" value={report.details} />}
        <FieldRow label="Status" value={labelFor(reportStatusLabels, report.status)} />
        {report.latest_note && <FieldRow label="Latest note" value={`${labelFor(actionLabels, report.latest_note.action)}: ${report.latest_note.reason}`} />}
      </dl>
      <h3>Reported content{content.removed ? ' (removed)' : ''}</h3>
      <ContentFields content={content} />
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="mod-actions">
        <div className="mod-action-row">
          {report.status === 'open' && <button type="button" className="button button-secondary" onClick={startReview}>Start review</button>}
          {waiting && <ReasonAction label="Dismiss report" confirmLabel="Dismiss report" onSubmit={run((reason) => setReportStatus(supabase, report.id, 'dismissed', reason))} />}
          {waiting && <ReasonAction label="Mark as actioned" confirmLabel="Mark as actioned" onSubmit={run((reason) => setReportStatus(supabase, report.id, 'actioned', reason))} />}
          {!waiting && <ReasonAction label="Reopen" confirmLabel="Reopen report" onSubmit={run((reason) => setReportStatus(supabase, report.id, 'reviewing', reason))} />}
        </div>
        <div className="mod-action-row">
          {content.available && !['user', 'organization'].includes(report.target_type) && !content.removed && (
            <ReasonAction label="Remove content" confirmLabel="Remove content" hint="The person who posted it will see this reason and can appeal."
              onSubmit={run((reason) => removeContent(supabase, { targetType: report.target_type, targetId: report.target_id, reason, reportId: report.id }))} />
          )}
          {content.removed && (
            <ReasonAction label="Restore content" confirmLabel="Restore content"
              onSubmit={run((reason) => restoreContent(supabase, { targetType: report.target_type, targetId: report.target_id, reason }))} />
          )}
          {content.author_user_id && !content.author_suspended && (
            <ReasonAction label="Suspend person" confirmLabel="Suspend person" withEndDate hint="They will see this reason and can appeal."
              onSubmit={run((reason, endsAt) => suspendAccount(supabase, { userId: content.author_user_id, reason, endsAt, reportId: report.id }))} />
          )}
          {content.organization_id && !content.organization_suspended && (
            <ReasonAction label="Suspend company" confirmLabel="Suspend company" withEndDate hint="Every member of the company will be stopped from posting and bidding."
              onSubmit={run((reason, endsAt) => suspendAccount(supabase, { organizationId: content.organization_id, reason, endsAt, reportId: report.id }))} />
          )}
        </div>
      </div>
    </article>
  );
}

function SuspensionsTab({ onChange, version }) {
  const [activeOnly, setActiveOnly] = useState(true);
  const { data, error } = useLoader(() => listSuspensions(supabase, activeOnly), [activeOnly, version]);
  const [kind, setKind] = useState('person');
  const [targetId, setTargetId] = useState('');
  const id = targetId.trim();
  return (
    <section>
      <div className="account-content-panel" style={{ marginTop: 0, marginBottom: 16 }}>
        <h2>Suspend by ID</h2>
        <p className="mod-note">Use the account or company ID from a report or from the Supabase dashboard.</p>
        <div className="mod-filters mod-form" style={{ background: 'transparent', border: 0, padding: 0 }}>
          <label>Suspend<select value={kind} onChange={(e) => setKind(e.target.value)}><option value="person">A person</option><option value="company">A company</option></select></label>
          <label>ID<input value={targetId} onChange={(e) => setTargetId(e.target.value)} autoComplete="off" /></label>
        </div>
        {id && !uuidPattern.test(id) && <p className="mod-note">Enter a full ID, for example 0a1b2c3d-0000-4000-8000-000000000000.</p>}
        {uuidPattern.test(id) && (
          <ReasonAction key={`${kind}:${id}`} label={kind === 'person' ? 'Suspend person' : 'Suspend company'} confirmLabel="Suspend" withEndDate
            onSubmit={async (reason, endsAt) => { await suspendAccount(supabase, kind === 'person' ? { userId: id, reason, endsAt } : { organizationId: id, reason, endsAt }); setTargetId(''); onChange(); }} />
        )}
      </div>
      <div className="mod-filters"><label className="mod-check"><input type="checkbox" checked={!activeOnly} onChange={(e) => setActiveOnly(!e.target.checked)} />Include lifted and ended suspensions</label></div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {data === null ? <p>Loading suspensions…</p> : data.length === 0 ? <p className="account-empty">No suspensions to show.</p> : (
        <ul className="mod-list">
          {data.map((row) => (
            <li key={row.id}>
              <strong>{row.name || (row.scope === 'company' ? 'Unnamed company' : 'Name not given')}</strong>{' '}
              <span className="coord-status">{row.scope === 'company' ? 'Company' : 'Person'} · {row.active ? 'Active' : row.lifted_at ? 'Lifted' : 'Ended'}</span>
              <p>{row.reason}</p>
              <p className="mod-note">
                From {formatDateTime(row.starts_at)}{row.ends_at ? ` until ${formatDateTime(row.ends_at)}` : ', no end date'}
                {row.appeal_status ? ` · Appeal ${row.appeal_status}` : ''}
                {row.lifted_at ? ` · Lifted ${formatDateTime(row.lifted_at)}: ${row.lift_reason}` : ''}
              </p>
              {row.active && <ReasonAction label="Lift suspension" confirmLabel="Lift suspension" onSubmit={async (reason) => { await liftSuspension(supabase, row.id, reason); onChange(); }} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AppealsTab({ onChange, version }) {
  const [status, setStatus] = useState('open');
  const { data, error } = useLoader(() => listAppeals(supabase, status === 'all' ? null : status), [status, version]);
  return (
    <section>
      <div className="mod-filters">
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">Open</option><option value="upheld">Upheld</option><option value="overturned">Overturned</option><option value="all">All</option>
        </select></label>
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {data === null ? <p>Loading appeals…</p> : data.length === 0 ? <p className="account-empty">No appeals to show.</p> : (
        <ul className="mod-list">
          {data.map((appeal) => {
            const overdue = appeal.status === 'open' && appeal.age_hours >= 24;
            return (
              <li key={appeal.id}>
                <strong>{appeal.kind === 'suspension' ? 'Suspension appeal' : `Removal appeal: ${labelFor(targetTypeLabels, appeal.target_type)}`}</strong>
                <span className="mod-note"> from {appeal.user_name || 'Name not given'} · waiting {formatAge(appeal.created_at)}</span>
                {overdue && <> <span className="mod-flag">Overdue</span></>}
                <p><b>Original reason:</b> {appeal.original_reason}</p>
                <p><b>Their appeal:</b> {appeal.statement}</p>
                {appeal.status === 'open' ? (
                  <div className="mod-action-row">
                    <ReasonAction label="Uphold decision" confirmLabel="Uphold" hint="The person will see this note." onSubmit={async (note) => { await decideAppeal(supabase, appeal.id, 'upheld', note); onChange(); }} />
                    <ReasonAction label="Overturn decision" confirmLabel="Overturn" hint={appeal.kind === 'suspension' ? 'This lifts the suspension. The person will see this note.' : 'This restores the content. The person will see this note.'}
                      onSubmit={async (note) => { await decideAppeal(supabase, appeal.id, 'overturned', note); onChange(); }} />
                  </div>
                ) : <p className="mod-note">{appeal.status === 'upheld' ? 'Upheld' : 'Overturned'} {formatDateTime(appeal.decided_at)}: {appeal.decision_note}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function AuditTab({ version }) {
  const { data, error } = useLoader(() => listActions(supabase, { limit: 300 }), [version]);
  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (data === null) return <p>Loading audit log…</p>;
  if (data.length === 0) return <p className="account-empty">No moderation actions yet.</p>;
  return (
    <div className="mod-table-wrap">
      <table className="mod-audit">
        <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Content</th><th>Reason</th></tr></thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.id}>
              <td>{formatDateTime(row.created_at)}</td>
              <td>{row.actor_name || (row.actor_id ? 'Staff member' : 'Owner (set up directly)')}</td>
              <td>
                {labelFor(actionLabels, row.action)}
                {row.details?.from ? ` (${labelFor(reportStatusLabels, row.details.from)} to ${labelFor(reportStatusLabels, row.details.to)})` : ''}
                {row.details?.decision ? ` (${row.details.decision})` : ''}
              </td>
              <td>{labelFor(targetTypeLabels, row.target_type)}{row.details?.title ? `: ${row.details.title}` : ''}</td>
              <td>{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
